/* eslint-disable prefer-template */
/* eslint-disable no-param-reassign */
/* eslint linebreak-style: ["error", "windows"] */
/**
 * ===========================
 * @author:  am2Studio      ==
 * @package: JS Bundle      ==
 * @version: 2.0.0          ==
 * ===========================
 *
 * @description: {
 * --- @usage {
 * a) plugins we develop for AM2 goes into plugins - reusable stuff
 * b) helpers will contain globally used functions, examples bellow
 * c) routes and its subfolders contains JS that applies for specific block/templates
 * d) vendor contains vendor JS - Should be enqueued with WP or lazyloaded with `loadJs`
 * }
 * --- @structure {
 * a) any plugin we create goes to `/plugins/` folder, should extend main component
 * b) any helper functions goes to `/helpers/` folder (debounce, parseData etc)
 * c) route specific js goes to `/routes/name/index.js` or if its functionality is large
 * you can create multiple files and import them in the index file
 * }
 *
 * @JSManager { The class that handles JS loading/running
 * - common js `init` key runs first
 * - route specific js `init`/`complete` keys runs after common (same order)
 * - common js `complete` key runs after
 * - any other lazyloaded JS runs last of course
 * }
 *
 * @loadJs {
 * - I've removed our old way of loading js since this one allows more flexibility
 * - and it allo allows to load multiple files then run your code, including css
 * - `https://github.com/muicss/loadjs`
 * }
 */

// JSManager and Helpers and other common JS
//import 'lazysizes';
//import 'lazysizes/plugins/bgset/ls.bgset';
import loadjs from 'loadjs';
import _JSManager from './am2-JSManager';
import helperDebounce from './helpers/debounce';
import helperParseData from './helpers/parseData';
import helperGenParams from './helpers/createParams';

// Common JS
import common from './routes/common';

// Blocks
import am2Tabs from './routes/blocks/tabs';
import am2Slider from './routes/blocks/slider';
import am2Accordion from './routes/blocks/accordion';

/**
 * Routed JS - loaded on specific block/template (if it exists in body class)
 * @see - class-am2-hooks.php @ body_class() filter function
 * @info - each block will generate a class like: am2-{ acf_name } where
 * acf_name is your ACF field name in Gutenberg editor. This class can be used as `am2AcfName`
 * Inside the routes bellow, as a key. Any JS located there will be run only when that block exists
 * @see - bellow you have various examples with normal/lazyloaded routed JS {
 * - this means, that the js will only be loaded on that page if the block exists
 * - if you need to have the JS in main bundle, then you call it
 * as a function (tabs/accordion) or object (slider) if you export
 * an object with init/complete/extends key from the route file
 * - if you consider that the js should be lazyloaded, use import() function
 * That will generate a chunk that if used in pair with functionality above,
 * will be loaded only when necessary. See maps js.
 * }
 */

// eslint-disable-next-line func-names
(function (am2) {
  // Attach Some Required Plugins
  am2.fn = {};
  am2.fn.loadJs = loadjs;
  am2.fn.debounce = helperDebounce;
  am2.fn.getOptions = helperParseData;
  am2.fn.createParams = helperGenParams;

  // Attach Specific Route JS
  am2 = {
    ...am2,
    ...{
      routes: {
        /**
         * @description
         * This prop includes the global JS fired on every page
         * All the js that needs to be loaded everywhere should be placed in `common` folder
         * There you can split your files into multiple parts based on your needs
         * @reserved Key
         */
        common,
        // Route created before using handleBodyClasses() in the common js
        // IE JS, runs only if is IE detected - just for demo that you can do that also
        // This route should be removed on production
        isIE: {
          // eslint-disable-next-line no-console
          init: () => console.log('Update this ancient tool.'),
          // eslint-disable-next-line no-alert
          complete: () => alert('Still not updated this browser ?'),
          // Lets extend if for EDGE Too
          extends: ['is-EDGE'],
        },
        // Slider - is an object with init/complete or extends key, imported above
        am2Slider,
        // Tabs - Is a function imported above, we call it on init/complete
        am2Tabs: {
          init: () => am2Tabs(),
        },
        // Accordion - Is a function imported above
        am2Accordion: {
          init: () => am2Accordion(),
        },
        // Maps - an example of lazyloaded route - WebPack will make a chunk and autoload it.
        // It will contain the JS for the plugin and also the function to initialize it.
        // You can also do it as the `am2Slider` above but using import(), but since
        // I wanted to pack everything (plugin + init object in a chunk) I did it like bellow
        // Important: for a propper output give your chunk a name like bellow
        // It will be used to output your built js where it should, which is js/routes/name
        am2GoogleMap: {
          complete: () => import(/* webpackChunkName: "js/routes/gmaps" */ './routes/blocks/gmaps').then(() => {
            // eslint-disable-next-line no-console
            console.log('Google Maps JS LazyLoaded - Since is lazyloaded, it will be after all JS (init/complete).');
          }),
        },
      },
    },
  };

  /**
   * Init the awesomeness itself
   */
  const JSManager = new _JSManager(am2);

  document.addEventListener('DOMContentLoaded', JSManager.loadEvents());
}).apply(this, [window.am2]);
